<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import { emitter } from 'shared/helpers/mitt';
import { catalogue, accountApps, loadApps, TOOL_ACCOUNT, TOOL_IDS } from './customerToolsCatalogue';
import { CUSTOMER_TOOLS_REQUEST, CUSTOMER_TOOLS_SELECT, CUSTOMER_TOOLS_STATE, scopeKey, positiveId, appChoices } from './customerToolsState.mjs';
const props = defineProps({ collapsed: { type: Boolean, default: false } });
const emit = defineEmits(['selected']);
const route = useRoute();
const icons = { '1': 'i-lucide-shopping-bag', '2': 'i-lucide-mail', '3': 'i-lucide-message-square', '4': 'i-lucide-truck', '5': 'i-lucide-store' };
const available = ref(null);
const account = computed(() => positiveId(route.params.accountId));
const scope = computed(() => scopeKey(account.value, route.params.conversation_id ?? route.params.conversationId));
const choices = computed(() => appChoices(accountApps(account.value)).filter(app => TOOL_IDS.includes(app.id)));
const enabled = computed(() => Boolean(scope.value && available.value?.scope === scope.value && catalogue.ready && catalogue.account === account.value));
function receive(state) {
  // Availability is identity-only; stale mounted views cannot enable this route.
  if (state?.scope === scope.value && scope.value) available.value = state;
  else if (state?.session === available.value?.session) available.value = null;
}
function select(appId) {
  if (!enabled.value || !available.value.appIds.includes(appId)) return;
  const request = { scope: scope.value, session: available.value.session, appId };
  emitter.emit(CUSTOMER_TOOLS_SELECT, request);
  if (available.value?.session === request.session && available.value.activeAppId === appId) emit('selected');
}
watch(account, value => { available.value = null; loadApps(value); }, { immediate: true });
watch(scope, () => { available.value = null; emitter.emit(CUSTOMER_TOOLS_REQUEST); }, { flush: 'post' });
onMounted(() => { emitter.on(CUSTOMER_TOOLS_STATE, receive); emitter.emit(CUSTOMER_TOOLS_REQUEST); });
onBeforeUnmount(() => { emitter.off(CUSTOMER_TOOLS_STATE, receive); });
</script>

<template>
  <section v-if="account === TOOL_ACCOUNT" aria-label="Customer tools" class="mt-3 min-w-0">
    <h2 :class="props.collapsed ? 'sr-only' : 'px-2 mb-2 text-xs font-semibold text-n-slate-11'">Customer tools</h2>
    <p v-if="!enabled" :class="props.collapsed ? 'sr-only' : 'px-2 mb-2 text-xs text-n-slate-11'" role="status">
      {{ scope ? 'Customer tools are loading or unavailable.' : 'Open a conversation to use customer tools.' }}
    </p>
    <ul class="flex flex-col gap-1 m-0 list-none">
      <li v-for="app in choices" :key="app.id">
        <button type="button"
          class="flex items-center gap-2 w-full rounded-lg px-2 py-2 text-sm text-n-slate-11 hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand disabled:opacity-50 disabled:cursor-not-allowed"
          :class="[{ 'bg-n-alpha-2 text-n-brand': enabled && available?.activeAppId === app.id }, props.collapsed ? 'justify-center' : 'text-start']"
          :disabled="!enabled || !available?.appIds.includes(app.id)"
          :aria-label="app.title"
          :title="enabled ? app.title : 'Open a conversation to use ' + app.title"
          :aria-pressed="enabled && available?.activeAppId === app.id"
          @click="select(app.id)">
          <span :class="icons[app.id]" class="size-4 flex-shrink-0" aria-hidden="true" />
          <span :class="props.collapsed ? 'sr-only' : 'truncate'">{{ app.title }}</span>
        </button>
      </li>
    </ul>
  </section>
</template>
